import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
} from "react";

import { useRouter } from "next/router";

import { AuthContext, AuthContextType } from "@/auth/context";
import { getBrowserToken, useAuthStore } from "@/auth/store";

/** Refresh the 5-minute browser token well before it lapses. */
const BROWSER_TOKEN_REFRESH_MS = 4 * 60 * 1000;

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const {
    isReady,
    token,
    signedIn,
    setToken,
    reset,
    userId,
    setUserId,
    deviceId,
    setDeviceId,
    setReady,
  } = useAuthStore();

  /**
   * Set the ready state to true once the component mounts.
   * This is used to prevent the app from rendering before the auth state is determined.
   * @returns void
   */
  useEffect(() => {
    setReady();
  }, [setReady]);

  /**
   * WHO-6344: keep a fresh browser token in the store while signed in, so
   * code that reads `token` from the context finds a valid one. Gateway
   * calls through Apollo await `getBrowserToken` themselves.
   */
  useEffect(() => {
    if (!signedIn) {
      return;
    }

    void getBrowserToken();
    const interval = window.setInterval(
      () => void getBrowserToken(),
      BROWSER_TOKEN_REFRESH_MS
    );
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void getBrowserToken();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [signedIn]);

  /**
   * Logout should remove token from authStore, clear apollo cache and redirect to home page if pathname isn't provided.
   * @param noRedirect - if true, redirect will be skipped
   * @param pathname - if provided the user will be redirected to this route after logout.
   * @param query - URL query object.
   * @returns void
   */
  const logout = useCallback<AuthContextType["logout"]>(
    ({ noRedirect, pathname, query } = {}) => {
      reset();

      if (noRedirect) {
        return;
      }

      router.push({
        pathname: pathname ?? "/",
        query,
      });
    },
    [router, reset]
  );

  /**
   * Memoize the context value to prevent unnecessary re-renders
   * @returns AuthContextType
   */
  const value = useMemo(
    () => ({
      isReady,
      isLoggedIn: signedIn,
      token,
      setToken,
      reset,
      userId,
      setUserId,
      deviceId,
      setDeviceId,
      logout,
    }),
    [
      isReady,
      logout,
      reset,
      signedIn,
      setToken,
      setUserId,
      token,
      userId,
      deviceId,
      setDeviceId,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
